"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { AlertCircle, ArrowLeft, Check, CreditCard, X } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import PATHS from "@/routes/paths";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { useBuyCreditsStore } from "@/features/finances/store/useBuyCreditsStore";
import { useFinancesStore } from "@/features/finances/store/useFinancesStore";

interface CheckoutStatusData {
  session_id: string;
  payment_id: string;
  status: "open" | "complete" | "expired";
  payment_status: "unpaid" | "paid" | "no_payment_required";
  is_completed: boolean;
  payment_intent_id: string;
  payment_intent_status: string;
  expires_at: number;
  credits: number;
  available_credits: number;
  amount: number;
  currency: string;
  pricing: {
    total_price_cents: number;
    price_per_credit_cents: number;
  };
  tier: {
    min_credits: number;
    max_credits: number;
    description: string;
  };
  savings: {
    savings_amount_cents: number;
    savings_amount_formatted: string;
    savings_percent: number;
    has_savings: boolean;
  };
}

export default function CheckoutStatusPage() {
  const { t } = useTranslation(["finances", "common"]);
  const { sessionId, paymentId } = useParams<{ sessionId: string; paymentId: string }>();
  const [statusData, setStatusData] = useState<CheckoutStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { clearData } = useBuyCreditsStore();
  const { refetch } = useFinancesStore();

  // Cleanup store data on unmount
  useEffect(() => {
    return () => {
      refetch();
      clearData();
    };
  }, [clearData]);

  useEffect(() => {
    const fetchCheckoutStatus = async () => {
      if (!sessionId || !paymentId) {
        setError(t("finances:checkout.errors.missingParams", "Missing required parameters"));
        setIsLoading(false);

        return;
      }

      try {
        setIsLoading(true);
        setError("");

        const response = await axiosApiInstance.get(API_ENDPOINTS.TUTOR.FINANCES.CHECKOUT_STATUS, {
          params: {
            session_id: sessionId,
            payment_id: paymentId,
          },
        });

        const checkoutData = response.data.data;

        setStatusData(checkoutData);
      } catch (err: any) {
        console.error("Failed to fetch checkout status:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            t("finances:checkout.errors.fetchFailed", "Failed to fetch checkout status"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCheckoutStatus();
  }, [sessionId, paymentId]);

  // Determine if payment was successful
  const isPaymentSuccessful = statusData?.payment_status === "paid" && statusData?.is_completed;
  const isPaymentFailed = statusData?.payment_status === "unpaid" || !statusData?.is_completed;

  if (isLoading) {
    return (
      <>
        <title>{t("finances:checkout.pageTitle", "Thank You for Purchase! - Ximly")}</title>
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="relative mx-auto max-w-4xl p-4 lg:p-6">
            {/* Background circles */}
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0 }}
            />
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute top-25 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0.2 }}
            />

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Header Skeleton */}
              <div className="mb-8 text-center">
                <Skeleton className="mx-auto mb-4 h-16 w-16 rounded-full" />
                <Skeleton className="mx-auto mb-2 h-8 w-64" />
                <Skeleton className="mx-auto h-6 w-48" />
              </div>
              {/* Main Content Skeleton */}
              <div className="space-y-6">
                <Skeleton className="h-32 w-full rounded-xl" />
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-24 w-full rounded-xl" />
              </div>
            </motion.div>
          </div>
        </ScrollArea>
      </>
    );
  }

  if (error || !statusData) {
    return (
      <>
        <title>{t("finances:checkout.pageTitle", "Thank You for Purchase! - Ximly")}</title>
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="relative mx-auto max-w-4xl p-4 lg:p-6">
            {/* Background circles */}
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0 }}
            />
            <motion.div
              animate={{ opacity: 0.2 }}
              className="absolute top-25 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
              initial={{ opacity: 0.2 }}
            />

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="mb-6 flex items-center gap-4">
                <Button asChild size="sm" variant="ghost">
                  <Link to={PATHS.TUTOR.FINANCES.BUY_CREDITS}>
                    <ArrowLeft className="h-4 w-4" />
                    {t("common:back", "Back")}
                  </Link>
                </Button>
              </div>

              <Card className="bg-side-accent/30 relative overflow-hidden rounded-2xl border-none p-8 shadow-xl">
                <div className="relative text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <h1 className="text-accent-foreground text-3xl font-bold">
                    {t("finances:checkout.error.title", "Error")}
                  </h1>
                  <p className="text-muted-foreground mt-2 text-lg">{error}</p>
                  <div className="mt-6">
                    <Button asChild>
                      <Link to={PATHS.TUTOR.FINANCES.BUY_CREDITS}>
                        {t("finances:checkout.tryAgain", "Try Again")}
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </ScrollArea>
      </>
    );
  }

  return (
    <>
      <title>
        {isPaymentSuccessful
          ? t("finances:checkout.pageTitle", "Thank You for Purchase! - Ximly")
          : t("finances:checkout.failedPageTitle", "Payment Failed - Ximly")}
      </title>
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="relative mx-auto max-w-4xl p-4 lg:p-6">
          {/* Background circles */}
          <motion.div
            animate={{ opacity: 0.2 }}
            className="absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors duration-700"
            initial={{ opacity: 0 }}
          />
          <motion.div
            animate={{ opacity: 0.2 }}
            className="absolute top-25 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors duration-700"
            initial={{ opacity: 0.2 }}
          />
          {/* Header Section */}
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mb-6 flex items-center justify-between">
              <Button asChild size="sm" variant="ghost">
                <Link to={PATHS.TUTOR.FINANCES.OVERVIEW}>
                  <ArrowLeft className="h-4 w-4" />
                  {t("finances:checkout.backToFinances", "Back to Finances")}
                </Link>
              </Button>
              {isPaymentSuccessful && (
                <Button asChild size="sm" variant="ghost">
                  <Link to={PATHS.TUTOR.FINANCES.BUY_CREDITS}>
                    <Icon className="h-4 w-4" icon="tabler:coins" />
                    {t("finances:checkout.buyMoreCredits", "Buy More Credits")}
                  </Link>
                </Button>
              )}
            </div>

            <Card className="bg-side-accent/30 relative overflow-hidden rounded-2xl border-none p-8 shadow-xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
              </div>

              <div className="relative text-center">
                <div
                  className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                    isPaymentSuccessful ? "bg-green-500/10" : "bg-red-500/10"
                  }`}
                >
                  {isPaymentSuccessful ? (
                    <Check className="h-8 w-8 text-green-500" />
                  ) : (
                    <X className="h-8 w-8 text-red-500" />
                  )}
                </div>
                <h1 className="text-accent-foreground text-3xl font-bold md:text-4xl">
                  {isPaymentSuccessful
                    ? t("finances:checkout.thankYouTitle", "Thank You for Purchase!")
                    : t("finances:checkout.paymentFailedTitle", "Payment Failed")}
                </h1>
                <p className="text-muted-foreground mt-2 text-lg">
                  {isPaymentSuccessful
                    ? t(
                        "finances:checkout.thankYouSubtitle",
                        "Your payment has been processed successfully",
                      )
                    : t(
                        "finances:checkout.paymentFailedSubtitle",
                        "Unfortunately, your payment could not be processed",
                      )}
                </p>
                {isPaymentFailed && (
                  <div className="mt-6">
                    <Button asChild>
                      <Link to={PATHS.TUTOR.FINANCES.BUY_CREDITS}>
                        {t("finances:checkout.tryAgain", "Try Again")}
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-foreground/50 relative overflow-hidden rounded-xl p-6 backdrop-blur-sm">
              {/* Background Pattern */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400/15 to-green-600/15" />
                <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-tr from-indigo-400/15 to-blue-500/15" />
              </div>

              <div className="relative space-y-6">
                {/* Key Metrics Grid */}
                <div
                  className={`grid grid-cols-1 gap-4 ${statusData.available_credits ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
                >
                  {/* Credits Purchased */}
                  <div className="bg-muted/20 rounded-lg p-4 text-center">
                    <div className="mb-2 flex justify-center">
                      <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                        <Icon
                          className="text-accent-foreground"
                          height="1.5rem"
                          icon="tabler:coins"
                          width="1.5rem"
                        />
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm font-medium">
                      {t("finances:checkout.credits", "Credits Purchased")}
                    </p>
                    <p className="text-accent-foreground text-2xl font-bold">
                      {statusData.credits}
                    </p>
                  </div>

                  {/* Total Price */}
                  <div className="bg-muted/20 rounded-lg p-4 text-center">
                    <div className="mb-2 flex justify-center">
                      <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                        <CreditCard className="text-accent-foreground h-6 w-6" />
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm font-medium">
                      {t("finances:checkout.details.totalPrice", "Total Paid")}
                    </p>
                    <p className="text-accent-foreground text-2xl font-bold">
                      {(statusData.pricing.total_price_cents / 100).toFixed(2)}{" "}
                      {statusData.currency.toUpperCase()}
                    </p>
                  </div>

                  {/* Current Balance */}
                  {statusData.available_credits && (
                    <div className="bg-muted/20 rounded-lg p-4 text-center">
                      <div className="mb-2 flex justify-center">
                        <div className="bg-muted/30 flex h-12 w-12 items-center justify-center rounded-lg">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="tabler:wallet"
                            width="1.5rem"
                          />
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("finances:checkout.currentCredits", "Current Balance")}
                      </p>
                      <p className="text-accent-foreground text-2xl font-bold">
                        {statusData.available_credits}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="bg-foreground/50 relative overflow-hidden p-6 backdrop-blur-sm">
              {/* Background Pattern */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -top-16 -left-16 h-32 w-32 rounded-full bg-purple-500/15" />
                <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-indigo-500/15" />
              </div>

              <div className="relative">
                <h3 className="text-accent-foreground mb-4 text-lg font-semibold">
                  {isPaymentSuccessful
                    ? t("finances:checkout.details.title", "Purchase Details")
                    : t("finances:checkout.details.attemptTitle", "Payment Attempt Details")}
                </h3>
                <div className="space-y-4">
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t("finances:checkout.summary.pricePerCredit", "Price per Credit")}
                      </span>
                      <span className="text-accent-foreground font-medium">
                        {(statusData.pricing.price_per_credit_cents / 100).toFixed(2)}{" "}
                        {statusData.currency.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="bg-muted/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t("finances:checkout.details.paymentStatus", "Payment Status")}
                      </span>
                      <span
                        className={`font-medium capitalize ${
                          isPaymentSuccessful ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {t(
                          `finances:checkout.details.${statusData.payment_status}`,
                          statusData.payment_status,
                        )}
                      </span>
                    </div>
                  </div>
                  {isPaymentSuccessful ? (
                    <div className="bg-muted/20 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Icon
                          className="text-muted-foreground"
                          height="1.25rem"
                          icon="tabler:mail"
                          width="1.25rem"
                        />
                        <span className="text-muted-foreground text-sm">
                          {t(
                            "finances:checkout.details.invoiceEmail",
                            "We will send you an invoice via email shortly",
                          )}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-muted/20 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Icon
                          className="text-red-500"
                          height="1.25rem"
                          icon="tabler:info-circle"
                          width="1.25rem"
                        />
                        <span className="text-muted-foreground text-sm">
                          {t(
                            "finances:checkout.details.paymentFailedInfo",
                            "No charges were made to your account. Please try again or contact support if the issue persists.",
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </ScrollArea>
    </>
  );
}
